import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Paper,
  IconButton,
  Menu,
  MenuItem
} from '@mui/material';
import { Icon as IconType } from 'lucide-react';
import InteractionControls from './InteractionControls.tsx';

interface EditableContentProps {
  title: string;
  content: string;
  icon?: IconType;
  cost?: number;
  onUpdate: (newData: { title?: string; content?: string; icon?: IconType; cost?: number }) => void;
  onGenerate?: () => void;
  availableIcons?: IconType[];
}

const EditableContent: React.FC<EditableContentProps> = ({
  title,
  content,
  icon,
  cost,
  onUpdate,
  onGenerate,
  availableIcons = []
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(title);
  const [editedContent, setEditedContent] = useState(content);
  const [editedCost, setEditedCost] = useState(cost);
  const [iconMenuAnchor, setIconMenuAnchor] = useState<null | HTMLElement>(null);

  const handleSave = () => {
    onUpdate({
      title: editedTitle,
      content: editedContent,
      cost: editedCost
    });
    setIsEditing(false);
  };

  const handleDelete = () => {
    onUpdate({
      title: '',
      content: '',
      cost: 0
    });
  };

  const handleGenerate = async () => {
    if (onGenerate) {
      await onGenerate();
    }
  };

  return (
    <Paper sx={{ p: 2, bgcolor: 'rgba(26, 26, 46, 0.7)' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {icon && (
            <IconButton 
              onClick={(e) => setIconMenuAnchor(e.currentTarget)}
              sx={{ color: '#00CC99' }}
            >
              {React.createElement(icon, { size: 20 })}
            </IconButton>
          )}
          {isEditing ? (
            <TextField
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              variant="standard"
              sx={{ color: '#fff' }}
            />
          ) : (
            <Typography variant="h6" sx={{ color: '#fff' }}>
              {title}
            </Typography>
          )}
        </Box>
        <InteractionControls
          isEditing={isEditing}
          onEdit={() => setIsEditing(true)}
          onSave={handleSave}
          onDelete={handleDelete}
          onGenerate={handleGenerate}
        />
      </Box>

      {isEditing ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            multiline
            rows={4}
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
            variant="outlined"
            sx={{ color: '#fff' }}
          />
          <TextField
            label="Cost"
            type="number"
            value={editedCost}
            onChange={(e) => setEditedCost(Number(e.target.value))}
            variant="outlined"
            sx={{ width: 200 }}
          />
        </Box>
      ) : (
        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          {content}
        </Typography>
      )}

      <Menu
        anchorEl={iconMenuAnchor}
        open={Boolean(iconMenuAnchor)}
        onClose={() => setIconMenuAnchor(null)}
      >
        {availableIcons.map((IconComponent, index) => (
          <MenuItem 
            key={index}
            onClick={() => {
              onUpdate({ icon: IconComponent });
              setIconMenuAnchor(null);
            }}
          >
            {React.createElement(IconComponent, { size: 20 })}
          </MenuItem>
        ))}
      </Menu>
    </Paper>
  );
};

export default EditableContent; 